import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import { notFound } from 'next/navigation';
import { memo } from 'react';

import { favoriteSymbolBunja } from '@flipster/features/src/shared/favorite-symbol/state';
import { FormattedMessage } from '@flipster/i18n';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { IconButton } from '@flipster/react/design-system/icon-button';
import Formatted from '@flipster/react/design-utils/formatted';
import DocumentTextIcon from '@flipster/react/icon/generated/DocumentTextIcon';
import StarFillIcon from '@flipster/react/icon/generated/StarFillIcon';
import StarOutlineIcon from '@flipster/react/icon/generated/StarOutlineIcon';
import { PathnameScope } from '@flipster/react/providers/route';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';

import { symbolDisplayPriceBunja, tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';
import { PipButton } from '@web/app/[locale]/(web)/trade/_misc/symbol-info/pip-button';

import { MarketListArea } from './market-list';

const symbolLeftBunja = bunja(() => {
  const { symbolInfoExistsAtom, isSpotAtom } = bunja.use(tradeSymbolBunja);
  const pathname = bunja.use(PathnameScope);
  const isSpotPath = pathname.includes('/spot/');

  const isNotFoundAtom = atom((get) => {
    if (!get(symbolInfoExistsAtom)) return false;
    const isSpot = Boolean(get(isSpotAtom));
    return isSpotPath !== isSpot;
  });

  return { isNotFoundAtom };
});
symbolLeftBunja.debugLabel = 'symbolLeftBunja';

export const SymbolLeft = memo(() => {
  const { isNotFoundAtom } = useBunja(symbolLeftBunja);
  const isNotFound = useAtomValue(isNotFoundAtom);
  if (isNotFound) notFound();

  return (
    <div
      className={flex({
        pos: 'relative',
        gap: { base: 8, tablet: 16 },
        flexDirection: { base: 'column', tablet: 'row' },
        alignItems: { tablet: 'center' },
        w: { tabletDown: '100%' },
      })}
    >
      <div className={flex({ alignItems: 'center', gap: 10 })}>
        <FavoriteButton />
        <MarketListArea />
        <ButtonsArea />
      </div>
      <div className={css({ color: 'content.default.level-5', minW: 150, mr: 16 })}>
        <TickerArea />
      </div>
    </div>
  );
});

const FavoriteButton = () => {
  const { favoriteAtom, handleFavoriteClick } = useBunja(favoriteSymbolBunja);
  const isFavorite = useAtomValue(favoriteAtom);
  return (
    <IconButton
      hierarchy='tertiary'
      size='small'
      icon={isFavorite ? <StarFillIcon /> : <StarOutlineIcon />}
      color={isFavorite ? 'content.state.warning' : 'content.default.level-1'}
      css={{ rounded: 4 }}
      onClick={handleFavoriteClick}
    />
  );
};

const ButtonsArea = () => {
  const { symbol, isSpotAtom, isFuturesAtom } = useBunja(tradeSymbolBunja);

  const isSpot = useAtomValue(isSpotAtom);
  const isFutures = useAtomValue(isFuturesAtom);

  return (
    <div className={flex({ gap: 10, tabletDown: { flex: 1, justifyContent: 'flex-end' } })}>
      <Tooltip placement='bottom'>
        <Tooltip.Trigger asChild>
          <IconButton
            hierarchy='tertiary'
            size='small'
            icon={<DocumentTextIcon />}
            as='a'
            href={isSpot ? `/support/spot-pairs/spec/${symbol}` : `/support/trading-rules/contract/${symbol}`}
            css={{ rounded: 4 }}
            newTab
          />
        </Tooltip.Trigger>
        <Tooltip.Content portal>
          {isSpot ? (
            <FormattedMessage id='Autogen_common.r251126.s_Spot_Pairs' />
          ) : (
            <FormattedMessage id='Autogen_common.r251126.s_Contracts' />
          )}
        </Tooltip.Content>
      </Tooltip>
      {isFutures && <PipButton />}
    </div>
  );
};

const TickerArea = () => {
  const {
    tickerExistsAtom,
    pricePrecisionAtom,
    displayPricePrecisionAtom,
    throttledDisplayPriceAtom,
    priceChange24hAtom,
    priceChangePct24hAtom,
  } = useBunja(symbolDisplayPriceBunja);

  const tickerExists = useAtomValue(tickerExistsAtom);
  const pricePrecision = useAtomValue(pricePrecisionAtom);
  const displayPricePrecision = useAtomValue(displayPricePrecisionAtom);
  const displayPrice = useAtomValue(throttledDisplayPriceAtom);
  const priceChange24h = useAtomValue(priceChange24hAtom);
  const priceChangePct24h = useAtomValue(priceChangePct24hAtom);

  if (!tickerExists) return;
  return (
    <>
      <Formatted.Number
        method='round-half-up'
        value={displayPrice}
        precision={displayPricePrecision}
        toFixed
        css={{
          textStyle: 'Title/XS/Monospace',
          color: Number(priceChangePct24h) >= 0 ? 'content.accent.buy' : 'content.accent.sell',
        }}
      />
      <div className={hstack({ textStyle: 'Text/M/Monospace/Regular', gap: 4 })}>
        <Formatted.Number value={priceChange24h} precision={pricePrecision} withColor toFixed />
        <Formatted.Percent value={priceChangePct24h} withColor toFixed withPlus />
      </div>
    </>
  );
};
